import React, { Component, ReactNode } from 'react'

export interface LottiePlayerErrorBoundaryProps {
  /**
   * @children - The lazy Lottie player tree guarded by this boundary.
   */
  children: ReactNode
  /**
   * @fallback - Rendered in place of the children once an error is caught.
   *             When omitted, nothing is rendered for the broken state.
   */
  fallback?: ReactNode
  /**
   * @onError - Called exactly once, the first time an error is caught.
   */
  onError?: (error: unknown) => void
}

interface LottiePlayerErrorBoundaryState {
  hasError: boolean
}

/**
 * Class-component error boundary for `LottiePlayer`. Catches load / parse /
 * render throws from the lazily-loaded `DotLottieReact` (corrupt ZIP, truncated
 * JSON, 404, non-Lottie payload) so a broken Lottie degrades to the `fallback`
 * tile instead of blanking or crashing the host surface. `onError` fires once.
 */
class LottiePlayerErrorBoundary extends Component<LottiePlayerErrorBoundaryProps, LottiePlayerErrorBoundaryState> {
  state: LottiePlayerErrorBoundaryState = { hasError: false }

  static getDerivedStateFromError(): LottiePlayerErrorBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: unknown): void {
    // Runs once for the caught error; after this the boundary renders `fallback`
    // (not the children), so the player can't re-throw and re-trigger onError.
    this.props.onError?.(error)
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return this.props.fallback ?? null
    }
    return this.props.children
  }
}

export default LottiePlayerErrorBoundary
